import { cloneElement, useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";

import type { OpenAPI } from "simplydo/interfaces";
import type { ChallengeWithIdeaTemplate } from "simplydo/interfaces/types/challengeTypes";
import type { IdeaWithAssessments } from "simplydo/interfaces/types/ideaTypes";
import { Menu, Popup } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { useTrayStatus } from "./Notifications";

export type PDFExportBaseProps = {
  trigger: React.ReactElement<{
    loading?: boolean;
    onClick?: (
      e?: React.MouseEvent<HTMLElement, MouseEvent>,
      ...args: unknown[]
    ) => void;
  }>;
  popupOffset?: [number, number];
};

type AssessmentRound = NonNullable<
  NonNullable<
    NonNullable<OpenAPI.Schemas["IdeaTemplate"]["assessments"]>["rounds"]
  >[number]
>;

type IdeaTemplateSection = NonNullable<
  NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number]
>;
type IdeaTemplateField = NonNullable<
  NonNullable<IdeaTemplateSection["fields"]>[number]
>;

type IdeaPdfExportProps = PDFExportBaseProps & {
  ideas: OpenAPI.Schemas["Idea"][];
  assessmentRound?: AssessmentRound;
  challenge?: ChallengeWithIdeaTemplate;
  hidePopup?: boolean;
};

const IdeaPdfExport = (props: IdeaPdfExportProps) => {
  const user = useAppSelector((state) => state.user);
  const {
    assessmentRound,
    ideas = [],
    trigger,
    popupOffset = [0, 0],
    challenge,
  } = props;
  const { t } = useTranslation();

  const [_, setTrayStatus] = useTrayStatus();

  const ideaIds = ideas.map((idea) => idea._id);

  const hasCreditsafeReport = useMemo(() => {
    if (!user || !challenge) {
      return false;
    }
    if (
      util.canManageChallenge(user, challenge) ||
      util.hasPermission(user, "challenge.viewIdeas", challenge._id)
    ) {
      const ideaWithTemplate = ideas.find(
        (idea): idea is IdeaWithAssessments => "ownerIdeaTemplate" in idea,
      );
      const ideaTemplate =
        challenge.ownerIdeaTemplate ?? ideaWithTemplate?.ownerIdeaTemplate;
      return ideaTemplate?.body?.find((section: IdeaTemplateSection) =>
        section?.fields?.find(
          (field: IdeaTemplateField) => field.type === "creditsafereport",
        ),
      );
    }
    return false;
  }, [challenge, ideas, user]);

  const generateReport = useCallback(
    (appendCreditsafeReport: boolean) => {
      if (!user) return;
      const challengeName = challenge?.name || "Challenge";
      let displayName = `${challengeName} idea export`;
      if (assessmentRound) {
        displayName += ` - ${assessmentRound.name}`;
      }
      api.users.createDownload(
        user._id,
        {
          downloadType: "ideaPdfExport",
          displayName: `${displayName}.pdf`,
          ideas: ideaIds,
          assessmentRoundId: assessmentRound?.id,
          challenge: challenge?._id,
          appendCreditsafeReport,
        },
        (download) => {
          api.setQueryData(
            api.users.getDownloads,
            [user._id],
            (prev) => ({
              ...prev,
              pendingDownloads: (prev?.pendingDownloads || []).concat([
                download,
              ]),
            }),
            { allowUndefined: true },
          );
          setTrayStatus({ open: true, activeIndex: 1 });
        },
        () => {},
      );
    },
    [ideaIds, challenge, user, setTrayStatus, assessmentRound],
  );

  if (hasCreditsafeReport) {
    return (
      <Popup
        content={<p>{t("ideas.options.download.pdf")}</p>}
        disabled={props.hidePopup}
        trigger={
          <Popup
            on="click"
            hoverable
            position="bottom center"
            trigger={cloneElement(trigger, {
              onClick: (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
                e?.preventDefault();
                e?.stopPropagation();
              },
            })}
            offset={popupOffset}
            content={
              <Menu secondary vertical>
                <Menu.Item header content="Export as..." />
                <Menu.Item
                  onClick={() => {}}
                  onMouseUp={() => generateReport(false)}
                >
                  Regular PDF
                </Menu.Item>
                <Menu.Item
                  key={"creditsafe"}
                  onClick={() => {}}
                  onMouseUp={() => generateReport(true)}
                >
                  With creditsafe Report
                </Menu.Item>
              </Menu>
            }
          />
        }
      />
    );
  }

  return (
    <Popup
      trigger={cloneElement(trigger, {
        onClick: () => {
          generateReport(false);
        },
      })}
      disabled={props.hidePopup}
      content={<p>{t("ideas.options.download.pdf")}</p>}
    />
  );
};

export default IdeaPdfExport;
